// Field catalogue for every stage form. ONE source of truth: StageForm renders from it, the backend
// profile / induction endpoints accept exactly these keys, and RETENTION_WORKFLOW.md §5.5 documents it.
//
// Field shape:
//   key      — request/response key (= DB column name)
//   store    — where it lives: profile (retention_student_profile) · student · ca (cro_assignment) · ce (course_enrollment)
//              · attempt (retention_demo_attempt) · report (retention_progress_report) · touchpoint · auto (derived, read-only)
//   type     — text | textarea | number | date | datetime | select | chips (one) | multi (many) | level (1–5) | yesno | readonly
//   options  — [{ value, label }] for select / chips / multi
//   source   — 'crm' prefilled from the hand-off · 'auto' computed by the system
//   kyc      — part of the KYC profile (Know your customer — KYC_260924.pdf); group = its KYC column
//   half     — renders at half width
//   required — must hold a value before the stage is signed off (stage 1: Mark verified; the API checks the same list)
//
// Codes stored as TINYINT follow the option values below; keep them in step with RETENTION_WORKFLOW_TABLES.md §5.

const o = (...labels) => labels.map((label, i) => ({ value: i + 1, label }));
const o0 = (...labels) => labels.map((label, i) => ({ value: i, label }));

export const LEVEL_LABELS = ['Very low', 'Low', 'Medium', 'High', 'Very high'];

export const OPTIONS = {
  callOutcome: o('Connected', 'No answer', 'Rescheduled', 'Wrong number'),
  locationTier: o('Urban', 'Semi-urban', 'Rural'),
  // student.gender stores the label itself (varchar)
  gender: ['Male', 'Female', 'Other'].map((g) => ({ value: g, label: g })),
  schoolType: o('Government', 'Aided', 'Unaided / private', 'International'),
  board: [
    { value: 'CBSE', label: 'CBSE' },
    { value: 'ICSE', label: 'ICSE' },
    { value: 'State', label: 'State' },
    { value: 'IGCSE', label: 'IGCSE' },
    { value: 'IB', label: 'IB' },
    { value: 'Other', label: 'Other' },
  ],
  stream: o('Science', 'Commerce', 'Humanities', 'Vocational', 'Not yet'),
  trend: o('Improving', 'Stable', 'Declining'),
  tuitionDependency: o('Low', 'Medium', 'High'),
  pathway: o('Higher education', 'Study abroad', 'Skill route', 'Employment', 'Undecided'),
  vark: o('Visual', 'Auditory', 'Reading / writing', 'Kinesthetic', 'Mixed'),
  motivation: o('Intrinsic', 'Parental', 'Peer', 'Fear'),
  aspirationClarity: o('Specific', 'Vague', 'None'),
  locusOfDecision: o('Student', 'Parent', 'Tuition teacher', 'Peer'),
  familyStructure: o('Nuclear', 'Joint'),
  migrantParent: o0('No', 'One parent abroad', 'Both parents abroad'),
  decisionHolder: o('Father', 'Mother', 'Both parents', 'Guardian', 'Student'),
  smartphone: o0('None', 'Shared', 'Personal'),
  internet: o('Poor', 'Average', 'Good'),
  device: o('Mobile', 'Tablet', 'Laptop', 'Desktop'),
  incomeType: o('Salaried', 'Self-employed', 'Daily wage', 'Agricultural', 'Remittance'),
  incomeStability: o('Stable', 'Variable', 'Uncertain'),
  lowMedHigh: o('Low', 'Medium', 'High'),
  paymentPreference: o('Lump sum', 'Instalments'),
  attendedBy: [
    { value: 'Parent', label: 'Parent' },
    { value: 'Student', label: 'Student' },
    { value: 'Both', label: 'Both' },
    { value: 'Neither', label: 'Neither' },
  ],
  inductionAttendance: o0('Absent', 'Attended', 'Partial', 'Rescheduled'),
  gradeLevel: o('Below grade', 'At grade', 'Above grade'),
  sentiment: o('Negative', 'Neutral', 'Positive'),
  renewalIntent: o0('No', 'Undecided', 'Yes'),
  changeRequested: o0('None', 'Tutor', 'Time slot', 'Pace', 'Other'),
  understanding: o('Below expected', 'As expected', 'Better than expected'),
  goalAchieved: o0('No', 'Partly', 'Yes'),
  likelyToReturn: o0('No', 'Maybe', 'Yes'),
};

const f = (key, label, type, extra = {}) => ({ key, label, type, ...extra });

export const STAGE_FIELDS = {
  // ------------------------------------------------------------------ 1 · Verify basics
  1: {
    save: 'profile',
    sections: [
      {
        title: 'Student & parent',
        icon: 'solar:user-id-bold-duotone',
        fields: [
          f('name', 'Student name', 'readonly', { store: 'student', source: 'crm', half: true }),
          // grade, date of birth, gender, school and board come from CRM but stay editable, so a KYC gap can be filled
          f('student_class_id', 'Current grade', 'select', { store: 'student', required: true, half: true, optionsFrom: 'student_class', kyc: 'Academic' }),
          f('dob', 'Date of birth', 'date', { store: 'student', required: true, half: true }),
          f('age', 'Age', 'readonly', { store: 'auto', source: 'auto', half: true, kyc: 'Demographic' }),
          f('gender', 'Gender', 'chips', { store: 'student', required: true, options: OPTIONS.gender, kyc: 'Demographic' }),
          // same student.father / student.mother as Student Edit; the student portal's Student Information card reads them
          f('father', 'Father name', 'text', { store: 'student', half: true }),
          f('mother', 'Mother name', 'text', { store: 'student', half: true }),
          f('contactNumber', 'Parent mobile', 'readonly', { store: 'student', source: 'crm', half: true }),
          f('whatsapp_number', 'WhatsApp number', 'text', { store: 'student', required: true, half: true, placeholder: 'Same as mobile?' }),
          f('time_zone', 'Time zone', 'select', {
            store: 'student',
            required: true,
            half: true,
            options: ['Asia/Kolkata', 'Asia/Dubai', 'Asia/Riyadh', 'Asia/Qatar', 'Asia/Kuwait', 'Asia/Bahrain', 'Asia/Muscat', 'Europe/London', 'America/New_York'].map((z) => ({ value: z, label: z })),
          }),
        ],
      },
      {
        title: 'School & region',
        icon: 'solar:buildings-2-bold-duotone',
        fields: [
          f('school_name', 'School name', 'text', { store: 'student', half: true, kyc: 'Demographic' }),
          f('syllabus', 'Board', 'select', { store: 'student', required: true, half: true, optionsFrom: 'student_syllabus', kyc: 'Demographic' }),
          f('school_type', 'School type', 'chips', { store: 'profile', options: OPTIONS.schoolType, kyc: 'Demographic' }),
          f('medium_of_instruction', 'Medium of instruction', 'text', { store: 'profile', half: true, kyc: 'Demographic' }),
          f('home_language', 'Home language', 'text', { store: 'profile', required: true, half: true, kyc: 'Demographic' }),
          f('district_region', 'District / region', 'text', { store: 'profile', required: true, half: true, kyc: 'Demographic' }),
          f('location_tier', 'Location tier', 'chips', { store: 'profile', options: OPTIONS.locationTier, kyc: 'Demographic' }),
        ],
      },
    ],
  },

  // ------------------------------------------------------------------ 2 · First call
  2: {
    save: 'profile',
    sections: [
      {
        title: 'Call',
        icon: 'solar:phone-calling-rounded-bold-duotone',
        fields: [
          f('first_call_date', 'Call date', 'datetime', { store: 'profile', required: true, half: true }),
          f('first_call_outcome', 'Outcome', 'chips', { store: 'profile', required: true, options: OPTIONS.callOutcome }),
        ],
      },
      {
        title: 'Goal',
        icon: 'solar:target-bold-duotone',
        fields: [
          f('parent_goal_id', 'Parent goal', 'chips', { store: 'profile', optionsFrom: 'retention_parent_goal' }),
          f('target_marks', 'Target marks', 'text', { store: 'profile', half: true }),
          f('target_exam', 'Target exam', 'text', { store: 'profile', half: true }),
        ],
      },
      {
        title: 'Academic profile',
        icon: 'solar:square-academic-cap-bold-duotone',
        fields: [
          f('stream', 'Stream', 'chips', { store: 'profile', options: OPTIONS.stream, kyc: 'Academic' }),
          f('last_exam_score', 'Latest assessment marks', 'text', { store: 'profile', half: true, kyc: 'Academic' }),
          f('performance_trend', 'Trend (last 3 cycles)', 'chips', { store: 'profile', options: OPTIONS.trend, kyc: 'Academic' }),
          f('strong_subjects', 'Strength map', 'text', { store: 'profile', half: true, kyc: 'Academic', placeholder: 'Subjects / topics' }),
          f('weak_subjects', 'Weakness map · subjects', 'text', { store: 'profile', half: true, kyc: 'Academic' }),
          f('weak_topics', 'Weakness map · topics', 'text', { store: 'profile', kyc: 'Academic' }),
          f('reading_fluency_level', 'Reading fluency', 'level', { store: 'profile', kyc: 'Academic' }),
          f('numeracy_level', 'Numeracy', 'level', { store: 'profile', kyc: 'Academic' }),
          f('english_proficiency_level', 'English proficiency', 'level', { store: 'profile', kyc: 'Academic' }),
          f('prior_tuition', 'Prior tuition', 'yesno', { store: 'profile', half: true }),
          f('tuition_hours_per_week', 'Tuition load (hrs / week)', 'number', { store: 'profile', half: true, kyc: 'Academic' }),
          f('tuition_dependency', 'Tuition dependency', 'chips', { store: 'profile', options: OPTIONS.tuitionDependency, kyc: 'Academic' }),
          f('co_curricular', 'Co-curricular', 'text', { store: 'profile', half: true, kyc: 'Academic' }),
          f('non_academic_strength', 'Non-academic strength', 'text', { store: 'profile', half: true, kyc: 'Academic' }),
          f('post_school_pathway', 'Post-school pathway', 'chips', { store: 'profile', options: OPTIONS.pathway, kyc: 'Academic' }),
        ],
      },
      {
        title: 'Learning & mindset',
        icon: 'solar:lightbulb-bolt-bold-duotone',
        fields: [
          f('learning_style', 'Learning modality (VARK)', 'chips', { store: 'profile', options: OPTIONS.vark, kyc: 'Psychographic' }),
          f('attention_span', 'Attention span', 'level', { store: 'profile', kyc: 'Psychographic' }),
          f('exam_anxiety_level', 'Exam anxiety', 'level', { store: 'profile', kyc: 'Psychographic' }),
          f('pressure_tolerance', 'Pressure tolerance', 'level', { store: 'profile', kyc: 'Psychographic' }),
          f('communication_confidence', 'Communication confidence', 'level', { store: 'profile', kyc: 'Psychographic' }),
          f('self_efficacy_level', 'Self-motivation', 'level', { store: 'profile', kyc: 'Psychographic' }),
          f('openness_outside_learning', 'Openness to learning outside school', 'level', { store: 'profile', kyc: 'Psychographic' }),
          f('motivation_source', 'Motivation source', 'chips', { store: 'profile', options: OPTIONS.motivation, kyc: 'Psychographic' }),
          f('career_aspiration_clarity', 'Career aspiration', 'chips', { store: 'profile', options: OPTIONS.aspirationClarity, kyc: 'Psychographic' }),
          f('career_aspiration', 'Aspiration (if any)', 'text', { store: 'profile', half: true, kyc: 'Psychographic' }),
          f('locus_of_decision', 'Who decides', 'chips', { store: 'profile', options: OPTIONS.locusOfDecision, kyc: 'Psychographic' }),
          f('reference_group', 'Reference group', 'text', { store: 'profile', half: true, kyc: 'Psychographic' }),
        ],
      },
      {
        title: 'Family',
        icon: 'solar:users-group-rounded-bold-duotone',
        fields: [
          f('family_structure', 'Family', 'chips', { store: 'profile', options: OPTIONS.familyStructure, kyc: 'Demographic' }),
          f('sibling_count', 'Siblings', 'number', { store: 'profile', half: true, kyc: 'Demographic' }),
          f('birth_order', 'Birth order', 'number', { store: 'profile', half: true, kyc: 'Demographic' }),
          f('migrant_parent_status', 'Parent abroad', 'chips', { store: 'profile', options: OPTIONS.migrantParent, kyc: 'Demographic' }),
          f('decision_holder', 'Decision-holder', 'chips', { store: 'profile', options: OPTIONS.decisionHolder, kyc: 'Demographic' }),
          f('special_needs_status', 'Special educational needs', 'yesno', { store: 'profile', half: true, kyc: 'Demographic' }),
          f('special_needs_detail', 'SEN detail', 'text', { store: 'profile', half: true, kyc: 'Demographic' }),
        ],
      },
      {
        title: 'Access',
        icon: 'solar:laptop-minimalistic-bold-duotone',
        fields: [
          f('smartphone_access', 'Smartphone', 'chips', { store: 'profile', options: OPTIONS.smartphone, kyc: 'Economic' }),
          f('laptop_access', 'Laptop / computer', 'yesno', { store: 'profile', half: true, kyc: 'Economic' }),
          f('device_type', 'Class device', 'chips', { store: 'profile', options: OPTIONS.device }),
          f('internet_quality', 'Internet reliability', 'chips', { store: 'profile', options: OPTIONS.internet, kyc: 'Economic' }),
          f('daily_screen_hours', 'Daily screen hours', 'number', { store: 'profile', half: true, kyc: 'Economic' }),
          f('language_comfort', 'Language comfort', 'text', { store: 'profile', half: true }),
        ],
      },
      {
        title: 'Notes',
        icon: 'solar:notes-bold-duotone',
        fields: [f('cro_notes', 'CRO notes', 'textarea', { store: 'profile' })],
      },
    ],
  },

  // ------------------------------------------------------------------ 3 · Induction + Enroll Subject
  3: {
    save: 'induction',
    sections: [
      {
        title: 'Induction',
        icon: 'solar:presentation-graph-bold-duotone',
        fields: [
          f('induction_date', 'Date & time', 'datetime', { store: 'ca', required: true, half: true }),
          f('induction_link', 'Meeting link', 'text', { store: 'ca', required: true, half: true }),
          f('induction_attended_by', 'Attended by', 'chips', { store: 'ca', options: OPTIONS.attendedBy }),
          f('induction_attendance_status', 'Attendance', 'chips', { store: 'ca', options: OPTIONS.inductionAttendance }),
          f('induction_questions_raised', 'Questions raised', 'textarea', { store: 'ca' }),
          f('induction_demo_confirmed', 'Demo confirmed', 'yesno', { store: 'ca', half: true }),
        ],
      },
      {
        title: 'Affordability · CRO only',
        icon: 'solar:wallet-money-bold-duotone',
        private: true,
        fields: [
          f('current_school_fee', 'Current school fee (year)', 'number', { store: 'profile', half: true, kyc: 'Economic' }),
          f('current_tuition_spend', 'Current tuition spend (month)', 'number', { store: 'profile', half: true, kyc: 'Economic' }),
          f('occupation', 'Occupation', 'text', { store: 'profile', half: true, kyc: 'Economic' }),
          f('income_type', 'Income type', 'chips', { store: 'profile', options: OPTIONS.incomeType, kyc: 'Economic' }),
          f('income_stability', 'Income stability', 'chips', { store: 'profile', options: OPTIONS.incomeStability, kyc: 'Economic' }),
          f('ability_to_pay', 'Ability to pay', 'chips', { store: 'profile', options: OPTIONS.lowMedHigh, kyc: 'Economic' }),
          f('willingness_to_pay', 'Willingness to pay', 'chips', { store: 'profile', options: OPTIONS.lowMedHigh, kyc: 'Economic' }),
          f('payment_preference', 'Payment preference', 'chips', { store: 'profile', options: OPTIONS.paymentPreference, kyc: 'Economic' }),
          f('sibling_education_load', 'Siblings in paid education', 'number', { store: 'profile', half: true, kyc: 'Economic' }),
        ],
      },
    ],
  },

  // ------------------------------------------------------------------ 4 · Tutor mapping (TutorMappingStage: enrol → session
  // preference → TutorMatchPanel → assign → POST /demo-attempt). These fields are the snapshot the attempt stores.
  4: {
    save: 'demoAttempt',
    sections: [
      {
        title: 'Session preference',
        icon: 'solar:calendar-bold-duotone',
        fields: [
          f('preferred_days', 'Days', 'multi', { store: 'attempt', options: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => ({ value: d, label: d })) }),
          f('preferred_time_slot', 'Time slot', 'text', { store: 'attempt', half: true }),
          f('tutor_gender_preference', 'Tutor gender', 'chips', { store: 'attempt', options: o('Male', 'Female', 'No preference') }),
          f('teaching_language', 'Teaching language', 'readonly', { store: 'attempt', source: 'crm', half: true }),
          f('session_duration', 'Session duration (min)', 'number', { store: 'attempt', half: true }),
        ],
      },
    ],
  },

  // ------------------------------------------------------------------ 5 · Demo class
  5: {
    save: 'schedule', // PUT /retention/demo-attempt/:id/schedule, one form per subject's open attempt
    sections: [
      {
        title: 'Demo',
        icon: 'solar:videocamera-record-bold-duotone',
        fields: [
          f('demo_datetime', 'Date & time', 'datetime', { store: 'attempt', required: true, half: true }),
          f('demo_duration', 'Duration (min)', 'number', { store: 'attempt', half: true }),
          // required: the parent's side starts here - the student portal shows the demo with a Join button from this save
          f('demo_link', 'Meeting link', 'text', { store: 'attempt', required: true, placeholder: 'https://meet.google.com/...' }),
        ],
      },
    ],
  },

  // ------------------------------------------------------------------ 6 · Demo feedback (6a tutor · 6b parent)
  6: {
    save: 'demoFeedback', // each section saves on its own: PUT .../tutor-feedback, then .../parent-feedback
    sections: [
      {
        title: 'Tutor feedback',
        save: 'tutorFeedback',
        icon: 'solar:square-academic-cap-bold-duotone',
        fields: [
          f('student_joined', 'Student joined', 'yesno', { store: 'attempt', half: true }),
          f('join_duration', 'Joined for (min)', 'number', { store: 'attempt', half: true }),
          f('topic_covered', 'Topic covered', 'text', { store: 'attempt' }),
          f('recording_link', 'Recording link', 'text', { store: 'attempt' }),
          f('tutor_student_level', 'Student level', 'chips', { store: 'attempt', options: OPTIONS.gradeLevel }),
          f('communication_skill_baseline', 'Communication skill baseline', 'level', { store: 'attempt', kyc: 'Academic' }),
          f('tutor_gaps_identified', 'Gaps identified', 'textarea', { store: 'attempt' }),
          f('sessions_per_week_advised', 'Sessions / week advised', 'number', { store: 'attempt', half: true }),
          f('demo_evaluation', 'Demo evaluation', 'text', { store: 'attempt' }),
        ],
      },
      {
        title: 'Parent feedback',
        save: 'parentFeedback',
        icon: 'solar:star-bold-duotone',
        fields: [
          f('parent_overall_rating', 'Overall', 'level', { store: 'attempt' }),
          f('parent_comfort_rating', 'Comfort with tutor', 'level', { store: 'attempt' }),
          f('parent_clarity_rating', 'Clarity of teaching', 'level', { store: 'attempt' }),
          f('parent_comments', 'Comments', 'textarea', { store: 'attempt' }),
          f('is_parent_happy', 'Continue with this tutor?', 'yesno', { store: 'attempt' }),
        ],
      },
    ],
  },

  // ------------------------------------------------------------------ 7 · Admission Confirmed / Go-live (checklist)
  7: {
    save: 'checklist', // GoLiveStage: each item stamps a tasks.json pair once (PUT /admission-checklist)
    sections: [
      {
        title: 'Parent side',
        icon: 'solar:checklist-minimalistic-bold-duotone',
        fields: [
          f('group_creation', 'WhatsApp group created', 'yesno', { store: 'ca' }),
          f('parental_guidelines', 'Parental guidelines sent', 'yesno', { store: 'ca' }),
          f('refund_policy', 'Refund policy accepted', 'yesno', { store: 'ca' }),
          f('fee_details_shared', 'Fee details shared', 'yesno', { store: 'ce' }),
          f('course_span', 'Fee plan / term explained', 'yesno', { store: 'ca' }),
        ],
      },
      {
        title: 'Tutor side',
        icon: 'solar:square-academic-cap-bold-duotone',
        fields: [
          f('materials_shared', 'Material sent to tutor', 'yesno', { store: 'ca' }),
          f('trainer_guidelines', 'Trainer guidelines', 'yesno', { store: 'ca' }),
          f('trainer_terms', 'Trainer terms', 'yesno', { store: 'ca' }),
          f('trainer_orientation', 'Trainer orientation', 'yesno', { store: 'ca' }),
        ],
      },
      {
        title: 'Schedule',
        icon: 'solar:calendar-mark-bold-duotone',
        fields: [f('first_session_date', 'First session', 'readonly', { store: 'ca', source: 'auto', half: true })],
      },
    ],
  },

  // ------------------------------------------------------------------ 8 · 1st feedback (parent + tutor + CRO)
  8: {
    save: 'firstFeedback',
    sections: [
      {
        title: 'Signals',
        icon: 'solar:pulse-2-bold-duotone',
        fields: [
          f('attendance_percent', 'Attendance', 'readonly', { store: 'auto', source: 'auto', half: true, kyc: 'Academic', suffix: '%' }),
          f('consistency_percent', 'Consistency (4 weeks)', 'readonly', { store: 'auto', source: 'auto', half: true, kyc: 'Academic', suffix: '%' }),
          f('at_risk_indicators', 'At-risk indicators', 'readonly', { store: 'auto', source: 'auto', kyc: 'Academic' }),
        ],
      },
      {
        title: 'Parent feedback',
        icon: 'solar:users-group-rounded-bold-duotone',
        fields: [
          f('satisfaction_score', 'Satisfaction', 'level', { store: 'touchpoint' }),
          f('tutor_punctuality_rating', 'Tutor punctuality', 'level', { store: 'touchpoint' }),
          f('student_engagement_rating', 'Engagement', 'level', { store: 'touchpoint' }),
          f('pace_of_teaching_rating', 'Pace of teaching', 'level', { store: 'touchpoint' }),
          f('change_requested', 'Change requested', 'chips', { store: 'touchpoint', options: OPTIONS.changeRequested }),
          f('issues_raised', 'Issues raised', 'textarea', { store: 'touchpoint' }),
        ],
      },
      {
        title: 'CRO action',
        icon: 'solar:hand-stars-bold-duotone',
        fields: [
          f('action_taken', 'Action taken', 'textarea', { store: 'touchpoint' }),
          f('feedback_points', 'Feedback points', 'textarea', { store: 'touchpoint' }),
        ],
      },
    ],
  },

  // ------------------------------------------------------------------ 9 · Mid report
  9: {
    save: 'report',
    reportType: 1,
    sections: [
      {
        title: 'Mid progress',
        icon: 'solar:chart-2-bold-duotone',
        fields: [
          // sessions held = trainer_attendance rows; topics and remarks are prefilled from the tutor's session log
          // and student_examination (TrackingStage → MidProgressSources), the CRO reviews and saves
          f('sessions_done', 'Sessions held', 'readonly', { store: 'auto', source: 'auto', half: true }),
          f('sessions_total', 'Total sessions', 'readonly', { store: 'auto', source: 'auto', half: true }),
          f('attendance_percent', 'Attendance', 'readonly', { store: 'auto', source: 'auto', half: true, suffix: '%' }),
          f('homework_completion_percent', 'Homework %', 'number', { store: 'report', half: true }),
          f('topics_completed', 'Topics completed', 'textarea', { store: 'report' }),
          f('tutor_remarks', 'Tutor remarks', 'textarea', { store: 'report' }),
        ],
      },
    ],
  },

  // ------------------------------------------------------------------ 10 · Feedback report + CRO suggestions
  10: {
    save: 'report',
    reportType: 2,
    sections: [
      {
        title: 'Tutor',
        icon: 'solar:square-academic-cap-bold-duotone',
        fields: [
          f('strengths', 'Strengths', 'textarea', { store: 'report' }),
          f('improvement_areas', 'Improvement areas', 'textarea', { store: 'report' }),
          f('tutor_recommendations', 'Recommendations', 'textarea', { store: 'report' }),
        ],
      },
      {
        title: 'CRO',
        icon: 'solar:user-id-bold-duotone',
        fields: [
          f('cro_suggestions', 'Suggestions', 'textarea', { store: 'report' }),
          f('action_plan', 'Action plan', 'textarea', { store: 'report' }),
        ],
      },
    ],
  },

  // ------------------------------------------------------------------ 11 · Comparison
  11: {
    save: 'report',
    reportType: 3,
    sections: [
      {
        title: 'Impact',
        icon: 'solar:graph-up-bold-duotone',
        fields: [
          f('improvement_percent', 'Improvement %', 'readonly', { store: 'auto', source: 'auto', half: true }),
          f('aspiration_achievement_gap', 'Aspiration–achievement gap', 'readonly', { store: 'auto', source: 'auto', half: true, kyc: 'Academic' }),
          f('school_marks_before', 'School marks before', 'text', { store: 'report', half: true }),
          f('school_marks_after', 'School marks after', 'text', { store: 'report', half: true }),
          f('goal_achieved', 'Goal achieved', 'chips', { store: 'report', options: OPTIONS.goalAchieved }),
          f('key_achievements', 'Key achievements', 'textarea', { store: 'report' }),
          f('parent_meeting_date', 'Parent meeting', 'datetime', { store: 'report', half: true }),
        ],
      },
    ],
  },

  // ------------------------------------------------------------------ 12 · Retention call
  12: {
    save: 'callAttempt',
    saveLabel: 'Log attempt',
    sections: [
      {
        title: 'Call',
        icon: 'solar:phone-rounded-bold-duotone',
        fields: [
          f('connected', 'Connected', 'yesno', { store: 'touchpoint', half: true }),
          f('parent_sentiment', 'Sentiment', 'chips', { store: 'touchpoint', options: OPTIONS.sentiment }),
          f('renewal_intent', 'Renewal intent', 'chips', { store: 'touchpoint', options: OPTIONS.renewalIntent }),
          f('plan_offered', 'Plan offered', 'text', { store: 'touchpoint', half: true }),
          f('upsell_offered', 'Upsell offered', 'text', { store: 'touchpoint', half: true }),
          f('next_followup_date', 'Next follow-up', 'date', { store: 'touchpoint', half: true }),
          f('qualitative_notes', 'Notes', 'textarea', { store: 'touchpoint' }),
        ],
      },
    ],
  },

  // ------------------------------------------------------------------ 13 · Renewal
  13: {
    save: 'renew',
    saveLabel: 'Mark retained & renewed',
    sections: [
      {
        title: 'Renewal',
        icon: 'solar:cup-star-bold-duotone',
        fields: [
          f('renewal_date', 'Renewal date', 'date', { store: 'ce', half: true }),
          f('extra_sessions', 'Sessions purchased', 'number', { store: 'ce', half: true }),
          f('extra_sale_amount', 'Renewal amount', 'number', { store: 'ce', half: true }),
          f('retained_same_tutor', 'Same tutor', 'yesno', { store: 'ce', half: true }),
          f('referral_given', 'Referral given', 'yesno', { store: 'ce', half: true }),
          f('added_to_community_group', 'Community group', 'yesno', { store: 'ce', half: true }),
          f('remarks', 'Remarks', 'text', { store: 'ce' }),
        ],
      },
    ],
  },
};

// The two stage 12 exits (touchpoint type 3 / 4) — POST /retention/:id/exit. Also marks the enrolment inactive.
export const EXIT_FIELDS = {
  3: {
    title: 'Unhappy, not retained',
    icon: 'solar:sad-circle-bold-duotone',
    sections: [
      {
        title: 'Dissatisfaction',
        icon: 'solar:sad-circle-bold-duotone',
        fields: [
          f('primary_reason_id', 'Primary reason', 'chips', { store: 'touchpoint', optionsFrom: 'retention_dissatisfaction' }),
          f('secondary_reason_ids', 'Other reasons', 'multi', { store: 'touchpoint', optionsFrom: 'retention_dissatisfaction' }),
          f('is_tutor_related', 'Tutor related', 'yesno', { store: 'touchpoint', half: true }),
          f('refund_requested', 'Refund requested', 'yesno', { store: 'touchpoint', half: true }),
          f('winback_possible', 'Win-back possible', 'yesno', { store: 'touchpoint', half: true }),
          f('winback_followup_date', 'Win-back follow-up', 'date', { store: 'touchpoint', half: true }),
          f('course_inactive_date', 'Inactive from', 'date', { store: 'ce', half: true }),
          f('qualitative_notes', 'Notes', 'textarea', { store: 'touchpoint' }),
        ],
      },
    ],
  },
  4: {
    title: 'Happy, leaving',
    icon: 'solar:smile-circle-bold-duotone',
    sections: [
      {
        title: 'Exit',
        icon: 'solar:smile-circle-bold-duotone',
        fields: [
          f('exit_reason_id', 'Reason', 'chips', { store: 'touchpoint', optionsFrom: 'retention_exit' }),
          f('likely_to_return', 'Likely to return', 'chips', { store: 'touchpoint', options: OPTIONS.likelyToReturn }),
          f('expected_return_month', 'Expected return (YYYY-MM)', 'text', { store: 'touchpoint', half: true }),
          f('nps_score', 'NPS (0–10)', 'number', { store: 'touchpoint', half: true }),
          f('willing_to_refer', 'Willing to refer', 'yesno', { store: 'touchpoint', half: true }),
          f('testimonial_consent', 'Testimonial consent', 'yesno', { store: 'touchpoint', half: true }),
          f('course_inactive_date', 'Inactive from', 'date', { store: 'ce', half: true }),
          f('qualitative_notes', 'Notes', 'textarea', { store: 'touchpoint' }),
        ],
      },
    ],
  },
};

// Keys each save endpoint accepts (the backend keeps an identical allow-list).
export const saveKeys = (stageNo) =>
  (STAGE_FIELDS[stageNo]?.sections || [])
    .flatMap((s) => s.fields)
    .filter((fl) => fl.type !== 'readonly' && fl.store !== 'auto')
    .map((fl) => fl.key);

// labels of the required fields still empty in `values` (stage sign-off gate)
export const missingRequired = (stageNo, values = {}) =>
  (STAGE_FIELDS[stageNo]?.sections || [])
    .flatMap((s) => s.fields)
    .filter((fl) => fl.required && (values[fl.key] === null || values[fl.key] === undefined || String(values[fl.key]).trim() === ''))
    .map((fl) => fl.label);
